import { z } from 'zod';
import { VENDOR_CATEGORIES } from '../constants.js';
import { auditFields, centsSchema, notesText, shortText, ulidSchema } from '../common.js';

/** The couple only sets `plannedCents`; actual and paid are derived from vendors and the venue. */
export const budgetCategorySchema = z.object({
  type: z.literal('BudgetCategory'),
  wedId: ulidSchema,
  category: z.enum(VENDOR_CATEGORIES),
  plannedCents: centsSchema.default(0),
  ...auditFields,
});
export type BudgetCategory = z.infer<typeof budgetCategorySchema>;

export const setPlannedSchema = z.object({ plannedCents: centsSchema });

export const budgetRowSchema = z.object({
  category: z.enum(VENDOR_CATEGORIES),
  plannedCents: centsSchema,
  actualCents: centsSchema,
  paidCents: centsSchema,
  remainingCents: z.number().int(),
});
export type BudgetRow = z.infer<typeof budgetRowSchema>;

export const budgetRollupSchema = z.object({
  wedId: ulidSchema,
  currency: z.string(),
  rows: z.array(budgetRowSchema),
  totals: budgetRowSchema.omit({ category: true }),
  /** Sum of payments due in the next 60 days that are not yet paid. */
  upcomingPaymentsCents: centsSchema,
  /**
   * The booked venue is being counted here because no supplier covers it. Its
   * payments cannot be marked off until it is added as one, so `paidCents`
   * understates until then and the UI has to say so.
   */
  venueUntracked: z.boolean(),
});
export type BudgetRollup = z.infer<typeof budgetRollupSchema>;

/** A scenario is "if we book this venue and these vendors, what does it cost?" */
export const scenarioSchema = z.object({
  type: z.literal('Scenario'),
  wedId: ulidSchema,
  scenarioId: ulidSchema,
  name: shortText,
  venueId: ulidSchema.optional(),
  vendorIds: z.array(ulidSchema).max(200).default([]),
  projectedCents: centsSchema.default(0),
  notes: notesText,
  ...auditFields,
});
export type Scenario = z.infer<typeof scenarioSchema>;

export const createScenarioSchema = scenarioSchema
  .omit({
    type: true,
    wedId: true,
    scenarioId: true,
    projectedCents: true,
    createdAt: true,
    updatedAt: true,
    updatedBy: true,
  })
  .partial()
  .required({ name: true });
export type CreateScenarioInput = z.infer<typeof createScenarioSchema>;
export const updateScenarioSchema = createScenarioSchema.partial();

export const scenarioProjectionSchema = z.object({
  scenarioId: ulidSchema,
  currency: z.string(),
  projectedCents: centsSchema,
  plannedCents: centsSchema,
  varianceCents: z.number().int(),
  byCategory: z.array(budgetRowSchema),
  /** Filled by the AI budget narrative when requested; numbers are never model-generated. */
  narrative: z.string().max(2000).optional(),
  /** Ids the scenario still names that no longer exist, so a stale pick is visible. */
  missing: z.array(z.string()).default([]),
});
export type ScenarioProjection = z.infer<typeof scenarioProjectionSchema>;
